import { useMemo } from "react";
import { useLocation } from "react-router";
import { styled } from "@pigment-css/react";
import { useAuthContext } from "@acto/web-common/auth";
import { useFlagWithContext } from "@acto/web-common/feature-flags";
import { buildPylonSupportUrl, PYLON_HELP_CENTER_URL } from "@acto/web-common/support";
import { useLocale, useTranslations } from "@acto/web-common/locales";
import { StandardPeriod } from "@acto/taskpane/core/domain";
import { useContributeChatScope, trendToRevenueSnapshot } from "@acto/taskpane/core/chat-envelope";
import ActoRoutes from "@acto/taskpane/core/router/routes.config";
import { ProfileMenu } from "@acto/taskpane/field-sales-2/components/patterns/ProfileMenu";
import { KpiHeadline } from "@acto/taskpane/field-sales-2/components/primitives/Card";
import { Skeleton } from "@acto/taskpane/field-sales-2/components/primitives/Skeleton";
import { GlassButton } from "@acto/taskpane/field-sales-2/components/primitives/GlassButton";
import { todayUtc } from "@acto/taskpane/field-sales-2/components/primitives/Calendar";
import { useCanAccessAnalysis } from "@acto/taskpane/field-sales-2/data-access/useCanAccessAnalysis";
import { useTenantKpiTrend } from "@acto/taskpane/field-sales-2/data-access/useTenantKpiTrend";
import { useDirectionalNavigate } from "@acto/taskpane/field-sales-2/utils/viewTransitions";
import { formatWeekdayDate } from "@acto/taskpane/field-sales-2/utils/format";

const Card = styled("section")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.fieldSalesTwo.spacing[20],
  margin: `0 ${theme.fieldSalesTwo.spacing[12]}`,
  padding: theme.fieldSalesTwo.spacing[24],
  paddingTop: theme.fieldSalesTwo.spacing[72],
  borderRadius: theme.fieldSalesTwo.radius["38"],
  background: theme.fieldSalesTwo.colors.surface.glass,
  border: `1px solid ${theme.fieldSalesTwo.colors.surface.glassBorder}`,
}));

const Header = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "flex-start",
  justifyContent: "space-between",
  gap: theme.fieldSalesTwo.spacing[16],
}));

const GreetingText = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.fieldSalesTwo.spacing[4],
  minWidth: 0,
}));

const DateLine = styled("span")(({ theme }) => ({
  fontFamily: theme.fieldSalesTwo.font.family,
  fontSize: theme.fieldSalesTwo.font.size[14],
  fontWeight: theme.fieldSalesTwo.font.weight[440],
  lineHeight: theme.fieldSalesTwo.font.lineHeight[148],
  color: theme.fieldSalesTwo.colors.neutral[700],
}));

const Greeting = styled("h1")(({ theme }) => ({
  margin: 0,
  fontFamily: theme.fieldSalesTwo.font.family,
  fontSize: theme.fieldSalesTwo.font.size[22],
  fontWeight: theme.fieldSalesTwo.font.weight[520],
  lineHeight: theme.fieldSalesTwo.font.lineHeight[148],
  color: theme.fieldSalesTwo.colors.neutral[900],
}));

const Revenue = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.fieldSalesTwo.spacing[4],
}));

const RevenueLabel = styled("span")(({ theme }) => ({
  fontFamily: theme.fieldSalesTwo.font.family,
  fontSize: theme.fieldSalesTwo.font.size[14],
  fontWeight: theme.fieldSalesTwo.font.weight[440],
  lineHeight: theme.fieldSalesTwo.font.lineHeight[148],
  color: theme.fieldSalesTwo.colors.neutral[700],
}));

const RevenueDetailsButton = styled(GlassButton)(({ theme }) => ({
  width: "100%",
  color: theme.fieldSalesTwo.colors.neutral[800],
}));

function greetingKey(hour: number): "morning" | "afternoon" | "evening" {
  if (hour < 12) return "morning";
  if (hour < 18) return "afternoon";
  return "evening";
}

/**
 * YTD tenant revenue plus the "Zu den Umsatzdetails" entry point. Rendered only for users who can
 * reach the analysis page, so the query never fires — and the value is never shown — to roles the
 * analysis route would just bounce back to Home.
 */
function RevenueSection({ year }: { year: number }) {
  const t = useTranslations("fieldSalesTwo.home.greeting");
  const tHome = useTranslations("fieldSalesTwo");
  const navigate = useDirectionalNavigate();
  const location = useLocation();

  // useTenantKpiTrend's shared query returns the comparison too (the analysis page renders it);
  // here we only use the headline total.
  const revenue = useTenantKpiTrend({ period: StandardPeriod.YEAR_TO_DATE });

  // Ambient context: the YTD headline is a number, not the chart — no series.
  const revenueFragment = trendToRevenueSnapshot(
    revenue.raw,
    { kind: "preset", preset: StandardPeriod.YEAR_TO_DATE },
    { includeSeries: false },
  );
  useContributeChatScope("home", revenueFragment);

  const handleOpenRevenueDetails = () => {
    navigate(ActoRoutes.ANALYSIS, { state: { from: location.pathname }, direction: "push" });
  };

  return (
    <>
      <Revenue>
        <RevenueLabel>{t("revenueLabel", { year })}</RevenueLabel>
        <KpiHeadline>
          {revenue.isPending ? <Skeleton.Line inline width="8rem" height="1.5em" /> : revenue.total}
        </KpiHeadline>
      </Revenue>
      <RevenueDetailsButton onClick={handleOpenRevenueDetails}>{tHome("revenueDetails")}</RevenueDetailsButton>
    </>
  );
}

export function HomeGreetingCard() {
  const t = useTranslations("fieldSalesTwo.home.greeting");
  const { locale } = useLocale();
  const { user, logout } = useAuthContext();
  const canAccessAnalysis = useCanAccessAnalysis();

  const isContactSupportEnabled = useFlagWithContext("contact-support", false);
  const navigate = useDirectionalNavigate();

  // Support is gated behind the flag and prefilled with the signed-in user; the help center is a
  // public link, always shown.
  const handleContactSupport = useMemo(() => {
    if (!isContactSupportEnabled || !user) return undefined;
    return () => {
      const url = buildPylonSupportUrl({ name: user.name, email: user.email });
      window.open(url, "_blank", "noopener,noreferrer");
    };
  }, [isContactSupportEnabled, user]);

  const handleOpenHelpCenter = () => {
    window.open(PYLON_HELP_CENTER_URL, "_blank", "noopener,noreferrer");
  };

  // The greeting bucket reads the local wall-clock hour; the date label and year are anchored to
  // todayUtc() (UTC midnight of the local calendar day) so they agree with the greeting near
  // midnight — the formatters are UTC-pinned, so a raw `new Date()` would render yesterday's day
  // for users east of UTC between local and UTC midnight.
  const today = todayUtc();
  const dateLabel = formatWeekdayDate(today, locale);
  const greeting = t(greetingKey(new Date().getHours()));

  return (
    <Card>
      <Header>
        <GreetingText>
          <DateLine>{dateLabel}</DateLine>
          <Greeting>{greeting}</Greeting>
        </GreetingText>
        {user && (
          <ProfileMenu
            userName={user.name ?? ""}
            userEmail={user.email ?? ""}
            onLogout={logout}
            onContactSupport={handleContactSupport}
            onOpenHelpCenter={handleOpenHelpCenter}
            onOpenProfile={() => navigate(ActoRoutes.USER_PROFILE, { direction: "push" })}
          />
        )}
      </Header>
      {canAccessAnalysis && <RevenueSection year={today.getUTCFullYear()} />}
    </Card>
  );
}
